/* eslint-disable max-lines */
import {
 useEffect, useRef, useCallback, useState
} from 'react';
import {
 Label
} from 'shared/components/ui/label';
import {
 EditorView, keymap, placeholder as cmPlaceholder, ViewUpdate
} from '@codemirror/view';
import {
 EditorState, Compartment
} from '@codemirror/state';
import {
    autocompletion, CompletionContext, CompletionResult, Completion
} from '@codemirror/autocomplete';
import {
    HighlightStyle, syntaxHighlighting, StreamLanguage
} from '@codemirror/language';
import {
 tags
} from '@lezer/highlight';

// --- OEL Built-in Functions ---
const OEL_FUNCTIONS: { name: string; signature: string; description: string }[] = [
    {
 name: 'upper',
signature: 'upper(x)',
description: 'Convert to uppercase'
},
    {
 name: 'lower',
signature: 'lower(x)',
description: 'Convert to lowercase'
},
    {
 name: 'trim',
signature: 'trim(x)',
description: 'Remove leading/trailing whitespace'
},
    {
 name: 'len',
signature: 'len(x)',
description: 'Get length of string/list/dict'
},
    {
 name: 'first',
signature: 'first(x)',
description: 'Get first element of list'
},
    {
 name: 'last',
signature: 'last(x)',
description: 'Get last element of list'
},
    {
 name: 'startswith',
signature: 'startswith(s, prefix)',
description: 'Check if string starts with prefix'
},
    {
 name: 'endswith',
signature: 'endswith(s, suffix)',
description: 'Check if string ends with suffix'
},
    {
 name: 'replace',
signature: 'replace(s, old, new)',
description: 'Replace substring'
},
    {
 name: 'is_null',
signature: 'is_null(x)',
description: 'Check if value is null/None'
},
    {
 name: 'coalesce',
signature: 'coalesce(a, b)',
description: 'Return a if not null, else b'
},
    {
 name: 'contains',
signature: 'contains(container, value)',
description: 'Check if container includes value'
},
];

const OEL_OPERATORS = ['==', '!=', '<', '<=', '>', '>=', 'and', 'or', 'not'];
const OEL_LITERALS = ['true', 'false', 'null', 'None', 'True', 'False'];

// --- Types ---
export type OELContext = {
    inputs: string[];
    previousStepIds: string[];
    loopVariables?: string[];
};

// =============================================================================
// Template <-> OEL conversion
// =============================================================================

/**
 * Convert OEL expression to template display string.
 * 'get the ' + @input.country + ' and an answer' → get the @input.country and an answer
 * If the OEL is complex (functions, operators, etc.), returns null to signal raw mode.
 */
function oelToTemplate(oel: string): string | null {
    if (!oel.trim()) return '';

    // Pure @reference
    if (/^@[A-Za-z_][A-Za-z0-9_.[\]]*$/.test(oel.trim())) return oel.trim();

    // Tokenize: walk the string pulling out quoted strings, @refs, and + operators
    const result: string[] = [];
    let i = 0;
    const s = oel.trim();

    while (i < s.length) {
        // Skip whitespace
        if (s[i] === ' ' || s[i] === '\t') { i++; continue; }

        // Skip + operator (the glue between parts)
        if (s[i] === '+') { i++; continue; }

        // Single-quoted string
        if (s[i] === "'") {
            const end = s.indexOf("'", i + 1);
            if (end === -1) return null; // malformed
            result.push(s.slice(i + 1, end));
            i = end + 1;
            continue;
        }

        // Double-quoted string
        if (s[i] === '"') {
            const end = s.indexOf('"', i + 1);
            if (end === -1) return null;
            result.push(s.slice(i + 1, end));
            i = end + 1;
            continue;
        }

        // @reference
        if (s[i] === '@') {
            const match = s.slice(i).match(/^@[A-Za-z_][A-Za-z0-9_.]*/);
            if (match) {
                result.push(match[0]);
                i += match[0].length;
                continue;
            }
        }

        // Anything else means it's a complex expression — bail
        return null;
    }

    return result.join('');
}

/**
 * Convert template display string to OEL expression.
 * get the @input.country and an answer → 'get the ' + @input.country + ' and an answer'
 */
function templateToOEL(template: string): string {
    if (!template) return '';

    const trimmed = template.trim();

    // Pure @reference
    if (/^@[A-Za-z_][A-Za-z0-9_.[\]]*$/.test(trimmed)) return trimmed;

    // Pure number
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) return trimmed;

    // Split by @references, build OEL parts
    const parts: { type: 'text' | 'ref'; value: string }[] = [];
    const regex = /@[A-Za-z_][A-Za-z0-9_.]*/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(template)) !== null) {
        if (match.index > lastIndex) {
            parts.push({
 type: 'text',
value: template.slice(lastIndex, match.index)
});
        }
        parts.push({
 type: 'ref',
value: match[0]
});
        lastIndex = regex.lastIndex;
    }
    if (lastIndex < template.length) {
        parts.push({
 type: 'text',
value: template.slice(lastIndex)
});
    }

    // If only one text part with no refs, just quote it
    if (parts.length === 1 && parts[0].type === 'text') {
        return `'${parts[0].value}'`;
    }

    return parts
        .map(p => p.type === 'text' ? `'${p.value}'` : p.value)
        .join(' + ');
}

// --- OEL Validation (for raw/expression mode only) ---
function validateOEL(text: string): string | null {
    if (!text.trim()) return null;

    if (/^@[A-Za-z_][A-Za-z0-9_.[\]]*$/.test(text.trim())) return null;
    if (/^'[^']*'$/.test(text.trim()) || /^"[^"]*"$/.test(text.trim())) return null;
    if (/^-?\d+(\.\d+)?$/.test(text.trim())) return null;
    if (/^(true|false|null|None|True|False)$/.test(text.trim())) return null;
    if (/^[a-zA-Z_]+\(/.test(text.trim())) return null;
    // Object / array literal, e.g. {"contents": [{"parts": [{"text": @input.x}]}]}.
    // Valid Python dict/list syntax — the engine evaluates these (ast.Dict / ast.List).
    if (/^[{[]/.test(text.trim())) return null;

    const OEL_KEYWORDS = /^(and|or|not|true|false|null|None|True|False)$/;
    const stripped = text
        .replace(/'[^']*'/g, '')
        .replace(/"[^"]*"/g, '')
        .replace(/@[A-Za-z_][A-Za-z0-9_.]*/g, '')
        .replace(/[+\-*/<>=!(),]/g, '')
        .replace(/\d+(\.\d+)?/g, '');

    const remaining = stripped.trim().split(/\s+/).filter(w => w && !OEL_KEYWORDS.test(w));
    if (remaining.length > 0) {
        return "Bare text must be quoted. Wrap strings in single quotes: '...' + @reference + '...'";
    }

    return null;
}

// --- Autocomplete ---
function buildOELCompletion(ctx: OELContext, isTemplateMode: boolean) {
    return (context: CompletionContext): CompletionResult | null => {
        const {
 state, pos
} = context;
        const line = state.doc.lineAt(pos);
        const textBefore = line.text.slice(0, pos - line.from);

        // 1) Triggered on `@` — show all root references
        const atMatch = textBefore.match(/@([A-Za-z_][A-Za-z0-9_]*)?$/);
        if (atMatch) {
            const from = pos - (atMatch[0].length);
            const typed = atMatch[1] || '';

            // In template mode, never wrap with quotes — just insert the reference
            let needsQuoteWrap = false;
            let wrapFrom = from;
            let wrapInsert = '';

            if (!isTemplateMode) {
                const beforeAt = textBefore.slice(0, textBefore.length - atMatch[0].length);
                needsQuoteWrap = beforeAt.trim().length > 0
                    && !beforeAt.trimEnd().endsWith("'")
                    && !beforeAt.trimEnd().endsWith('"')
                    && !beforeAt.trimEnd().endsWith('+')
                    && !beforeAt.trimEnd().endsWith('(')
                    && !beforeAt.trimEnd().endsWith(',');
                wrapFrom = needsQuoteWrap ? line.from : from;
                wrapInsert = needsQuoteWrap ? `'${beforeAt.trimEnd()}' + ` : '';
            }

            const options: Completion[] = [];

            // @input
            options.push({
                label: '@input',
                type: 'variable',
                detail: 'Opcode inputs',
                boost: 10,
                apply: needsQuoteWrap
                    ? (view, completion, fromPos, toPos) => {
                        view.dispatch({
                            changes: {
 from: wrapFrom,
to: toPos,
insert: wrapInsert + '@input'
}
                        });
                    }
                    : '@input',
            });

            // Previous step IDs
            ctx.previousStepIds.forEach(id => {
                const label = `@${id}`;
                options.push({
                    label,
                    type: 'variable',
                    detail: 'Step output',
                    boost: 5,
                    apply: needsQuoteWrap
                        ? (view, completion, fromPos, toPos) => {
                            view.dispatch({
                                changes: {
 from: wrapFrom,
to: toPos,
insert: wrapInsert + label
}
                            });
                        }
                        : label,
                });
            });

            // Loop variables
            (ctx.loopVariables || []).forEach(v => {
                const label = `@${v}`;
                options.push({
                    label,
                    type: 'variable',
                    detail: 'Loop variable',
                    boost: 7,
                    apply: needsQuoteWrap
                        ? (view, completion, fromPos, toPos) => {
                            view.dispatch({
                                changes: {
 from: wrapFrom,
to: toPos,
insert: wrapInsert + label
}
                            });
                        }
                        : label,
                });
            });

            return {
                from,
                options: options.filter(o => o.label.toLowerCase().includes(typed.toLowerCase())),
            };
        }

        // 2) After `@input.` — show input field names
        const inputDotMatch = textBefore.match(/@input\.([A-Za-z_][A-Za-z0-9_]*)?$/);
        if (inputDotMatch) {
            const from = pos - (inputDotMatch[1]?.length || 0);
            return {
                from,
                options: ctx.inputs.map(name => ({
                    label: name,
                    type: 'property',
                    detail: 'Input variable',
                })),
            };
        }

        // 3) After `@step_id.` — show `.output`
        const stepDotMatch = textBefore.match(/@([A-Za-z_][A-Za-z0-9_]*)\.([A-Za-z_][A-Za-z0-9_]*)?$/);
        if (stepDotMatch) {
            const stepId = stepDotMatch[1];
            if (stepId !== 'input' && ctx.previousStepIds.includes(stepId)) {
                const from = pos - (stepDotMatch[2]?.length || 0);
                return {
                    from,
                    options: [
                        {
 label: 'output',
type: 'property',
detail: 'Step output data'
},
                    ],
                };
            }
        }

        // 4) Function/keyword autocomplete — only in expression mode
        if (!isTemplateMode) {
            const wordMatch = textBefore.match(/\b([a-zA-Z_][a-zA-Z0-9_]*)$/);
            if (wordMatch && !textBefore.match(/@[A-Za-z_]*$/)) {
                const from = pos - wordMatch[1].length;
                const typed = wordMatch[1].toLowerCase();

                const options: Completion[] = [];

                OEL_FUNCTIONS.forEach(fn => {
                    if (fn.name.toLowerCase().startsWith(typed)) {
                        options.push({
                            label: fn.name,
                            type: 'function',
                            detail: fn.signature,
                            info: fn.description,
                            apply: fn.name + '(',
                        });
                    }
                });

                OEL_OPERATORS.forEach(op => {
                    if (op.startsWith(typed) && typed.length >= 2) {
                        options.push({
 label: op,
type: 'keyword'
});
                    }
                });

                OEL_LITERALS.forEach(lit => {
                    if (lit.toLowerCase().startsWith(typed)) {
                        options.push({
 label: lit,
type: 'constant'
});
                    }
                });

                if (options.length > 0) {
                    return {
 from,
options
};
                }
            }
        }

        return null;
    };
}

// --- Syntax highlighting: full OEL mode ---
const oelLanguage = StreamLanguage.define({
    token(stream) {
        if (stream.match(/@[A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)*/)) {
            return 'variableName';
        }
        if (stream.match(/'[^']*'/) || stream.match(/"[^"]*"/)) {
            return 'string';
        }
        if (stream.match(/-?\d+(\.\d+)?/)) {
            return 'number';
        }
        if (stream.match(/\b(and|or|not|true|false|null|None|True|False)\b/)) {
            return 'keyword';
        }
        if (stream.match(/\b(upper|lower|trim|len|first|last|startswith|endswith|replace|is_null|coalesce|contains)\s*\(/)) {
            stream.backUp(1);
            return 'function';
        }
        if (stream.match(/==|!=|<=|>=|[+\-*/<>]/)) {
            return 'operator';
        }
        stream.next();
        return null;
    },
});

// --- Syntax highlighting: template mode (only @references are highlighted) ---
const templateLanguage = StreamLanguage.define({
    token(stream) {
        if (stream.match(/@[A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)*/)) {
            return 'variableName';
        }
        stream.next();
        return null;
    },
});

const oelHighlightStyle = HighlightStyle.define([
    {
 tag: tags.variableName,
color: '#2563eb',
fontWeight: 'bold'
},
    {
 tag: tags.string,
color: '#16a34a'
},
    {
 tag: tags.number,
color: '#d97706'
},
    {
 tag: tags.keyword,
color: '#9333ea'
},
    {
 tag: tags.function(tags.variableName),
color: '#0891b2'
},
    {
 tag: tags.operator,
color: '#64748b'
},
]);

const templateHighlightStyle = HighlightStyle.define([
    {
 tag: tags.variableName,
color: '#2563eb',
fontWeight: 'bold'
},
]);

// --- Component ---
type OELCodeEditorProps = {
    label?: string;
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    multiline?: boolean;
    required?: boolean;
    helpText?: string;
    oelContext: OELContext;
    /** Template mode (default true): user types plain text with @references.
     *  Set to false for expression fields (conditions, numbers, etc.) */
    templateMode?: boolean;
};

const OELCodeEditor = ({
    label,
    value,
    onChange,
    placeholder,
    multiline = false,
    required = false,
    helpText,
    oelContext,
    templateMode = true,
}: OELCodeEditorProps) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const viewRef = useRef<EditorView | null>(null);
    const isUpdatingRef = useRef(false);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const templateModeRef = useRef(templateMode);
    templateModeRef.current = templateMode;

    // Track if we're in raw mode because OEL couldn't be converted to template
    const [isRawFallback, setIsRawFallback] = useState(false);
    const [warning, setWarning] = useState<string | null>(null);
    const completionCompartment = useRef(new Compartment());

    // Determine effective mode: template unless value is complex OEL
    const effectiveTemplate = templateMode && !isRawFallback;

    const defaultPlaceholder = effectiveTemplate
        ? 'Type text, use @ to insert references'
        : "'your text' + @input.name";

    // Convert OEL value → display value
    const toDisplay = useCallback((oel: string): string => {
        if (!templateMode) return oel;
        const tmpl = oelToTemplate(oel);
        if (tmpl === null) {
            // Complex OEL, can't show as template
            setIsRawFallback(true);
            return oel;
        }
        setIsRawFallback(false);
        return tmpl;
    }, [templateMode]);

    // Convert display value → OEL value
    const toOEL = useCallback((display: string): string => {
        if (!templateModeRef.current || isRawFallback) return display;
        return templateToOEL(display);
    }, [isRawFallback]);

    const completionExtension = useCallback(() => {
        return autocompletion({
            override: [buildOELCompletion(oelContext, effectiveTemplate)],
            activateOnTyping: true,
        });
    }, [oelContext.inputs.join(','), oelContext.previousStepIds.join(','), (oelContext.loopVariables || []).join(','), effectiveTemplate]);

    useEffect(() => {
        if (!editorRef.current) return;

        const minHeight = multiline ? '80px' : '36px';
        const maxHeight = multiline ? '200px' : '36px';

        const lang = effectiveTemplate ? templateLanguage : oelLanguage;
        const highlight = effectiveTemplate ? templateHighlightStyle : oelHighlightStyle;

        const state = EditorState.create({
            doc: toDisplay(value),
            extensions: [
                lang,
                syntaxHighlighting(highlight),
                completionCompartment.current.of(completionExtension()),
                cmPlaceholder(placeholder || defaultPlaceholder),
                ...(!multiline ? [keymap.of([{
                    key: 'Enter',
                    run: () => true,
                }])] : []),
                EditorView.updateListener.of((update: ViewUpdate) => {
                    if (update.docChanged && !isUpdatingRef.current) {
                        const newValue = update.state.doc.toString();
                        const cleanValue = multiline ? newValue : newValue.replace(/\n/g, '');
                        const oelValue = toOEL(cleanValue);
                        onChangeRef.current(oelValue);

                        // Only validate in raw/expression mode
                        if (!templateModeRef.current || isRawFallback) {
                            setWarning(validateOEL(oelValue));
                        } else {
                            setWarning(null);
                        }
                    }
                }),
                EditorView.theme({
                    '&': {
                        fontSize: '13px',
                        fontFamily: effectiveTemplate
                            ? '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                            : 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
                        minHeight,
                        maxHeight,
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '6px',
                        background: 'hsl(var(--background))',
                    },
                    '.cm-content': {
                        padding: '6px 10px',
                        minHeight,
                    },
                    '.cm-scroller': {
                        overflow: 'auto',
                        maxHeight,
                    },
                    '.cm-focused': {
                        outline: '2px solid hsl(var(--ring))',
                        outlineOffset: '-1px',
                        borderRadius: '6px',
                    },
                    '.cm-line': {
                        padding: '0',
                    },
                    '.cm-placeholder': {
                        color: 'hsl(var(--muted-foreground))',
                        fontStyle: 'italic',
                    },
                    '.cm-tooltip-autocomplete': {
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '6px',
                        background: 'hsl(var(--popover))',
                        color: 'hsl(var(--popover-foreground))',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                    },
                    '.cm-tooltip-autocomplete > ul > li': {
                        padding: '4px 8px',
                        fontSize: '12px',
                    },
                    '.cm-tooltip-autocomplete > ul > li[aria-selected]': {
                        background: 'hsl(var(--accent))',
                        color: 'hsl(var(--accent-foreground))',
                    },
                    '.cm-completionIcon': {
                        marginRight: '6px',
                        opacity: '0.7',
                    },
                    '.cm-completionDetail': {
                        fontStyle: 'normal',
                        opacity: '0.6',
                        fontSize: '11px',
                        marginLeft: '8px',
                    },
                }),
            ],
        });

        const view = new EditorView({
            state,
            parent: editorRef.current,
        });

        viewRef.current = view;

        return () => {
            view.destroy();
            viewRef.current = null;
        };
    }, [multiline, effectiveTemplate]);

    useEffect(() => {
        if (!viewRef.current) return;
        const displayValue = toDisplay(value);
        if (displayValue !== viewRef.current.state.doc.toString()) {
            isUpdatingRef.current = true;
            viewRef.current.dispatch({
                changes: {
                    from: 0,
                    to: viewRef.current.state.doc.length,
                    insert: displayValue,
                },
            });
            isUpdatingRef.current = false;
        }
    }, [value, toDisplay]);

    useEffect(() => {
        if (viewRef.current) {
            viewRef.current.dispatch({
                effects: completionCompartment.current.reconfigure(completionExtension()),
            });
        }
    }, [completionExtension]);

    return (
        <div className="space-y-1">
            {label && (
                <Label>
                    {label}
                    {required && <span className="text-destructive ml-1">*</span>}
                </Label>
            )}
            <div ref={editorRef} />

            {/* Warning for invalid OEL patterns (expression mode only) */}
            {warning && (
                <p className="text-xs text-amber-600 flex items-start gap-1">
                    <span className="shrink-0 mt-px">!</span>
                    <span>{warning}</span>
                </p>
            )}

            {/* Show generated OEL in template mode */}
            {effectiveTemplate && value && (
                <p className="text-xs text-muted-foreground font-mono truncate" title={value}>
                    OEL: {value}
                </p>
            )}

            {helpText ? (
                <p className="text-xs text-muted-foreground">{helpText}</p>
            ) : effectiveTemplate ? (
                <p className="text-xs text-muted-foreground">
                    Type text freely. Use <code className="bg-muted px-1 rounded">@</code> to insert references.
                </p>
            ) : (
                <p className="text-xs text-muted-foreground">
                    OEL expression: <code className="bg-muted px-1 rounded">'text ' + @input.name</code>
                </p>
            )}
        </div>
    );
};

export default OELCodeEditor;
export { OEL_FUNCTIONS, oelToTemplate, templateToOEL };
